import { cn } from "@-/com.npmjs.cn";
import { SkeletonTile } from "@-/dev.improve.haniverse.components.ui.skeleton";
import { CardMinimalTileUi } from "@-/dev.improve.haniverse.routes.nav.app._components.card.tile.minimal";
import type { CardTileType } from "@-/dev.improve.haniverse.routes.nav.app._components.card.tile";
import { MorphemeTilePresentationProvider } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.tile.ui.presentation";
import { useSectionAccordionRowWindow } from "@-/dev.improve.haniverse.components.ui.section-accordion";
import type { MorphemeDetailsTileSlice } from "@-/dev.improve.haniverse.lib.morpheme.entities";
import type {
  MorphemeGlyphTileStage,
  MorphemeStagesByGrapheme,
} from "@-/dev.improve.haniverse.lib.morpheme.stage";
import { useMorphemePanelsHrefNavigator } from "@-/dev.improve.haniverse.lib.morpheme.page.navigator";
import { useWindowDimensions, View } from "@-/com.npmjs.react-native";
import filledArray from "@-/com.npmjs.filled-array";
import { useCallback, useMemo, useState, type ReactElement } from "react";
import { userCardSelectionItem } from "@-/dev.improve.haniverse.routes.nav.app._context";
import { useAppSelection } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.tile.selection";
import type { CardContentKind } from "@-/dev.improve.haniverse.lib.cards.card-content";

type DeckCardMinimalTile = {
  cardType: CardTileType;
  contentKind: CardContentKind;
  /** A pronunciation card's own tone-number reading, which colours its glyph; else `null`. */
  pinyin: string | null;
  userCardId: string;
};

/** One of a front's study cards, as one tile of the grid. */
type DeckGlyphGridTile = DeckCardMinimalTile & { front: string };

type NavigateToMorpheme = (morpheme: string) => Promise<unknown>;

const DECK_MORPHEME_HREF = "/(nav)/(app)/decks/morpheme/[morpheme]";
const COLUMNS = 8;
const GAP = 4;
const HORIZONTAL_PADDING = 4;
const VERTICAL_PADDING = 4;

/**
 * The deck's compact grid, the same minimal card tiles as the Stats page's: one tile per study
 * card (a front with a meaning and a pronunciation card shows two), each with its card-type icon
 * above the glyph, in eight-column rows that mount only near the viewport.
 */
export function DeckCardSectionGlyphTilesView({
  cards,
  deckCardTilesByCharacter,
  footer,
  stageByGrapheme,
}: {
  cards: MorphemeDetailsTileSlice[];
  deckCardTilesByCharacter?: Map<string, DeckCardMinimalTile[]>;
  footer?: ReactElement | null;
  /** Stages derived from the deck's own actor rows, as the detailed grid paints them. */
  stageByGrapheme: MorphemeStagesByGrapheme;
}) {
  const { width: windowWidth } = useWindowDimensions();
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  const availableWidth = (containerWidth ?? windowWidth) - HORIZONTAL_PADDING * 2;
  const tileSize = Math.max(1, (availableWidth - GAP * (COLUMNS - 1)) / COLUMNS);
  const rowHeight = tileSize + GAP;
  const tiles = useMemo(
    () =>
      cards.flatMap((card) =>
        (deckCardTilesByCharacter?.get(card.front) ?? []).map((tile): DeckGlyphGridTile => ({
          ...tile,
          front: card.front,
        })),
      ),
    [cards, deckCardTilesByCharacter],
  );
  const rowCount = Math.ceil(tiles.length / COLUMNS);
  const { end, start } = useSectionAccordionRowWindow({
    offsetTop: VERTICAL_PADDING,
    rowCount,
    rowGap: GAP,
    rowHeight,
  });
  const rowsHeight = Math.max(0, rowCount * rowHeight - GAP);
  const topSpacerHeight = Math.min(rowsHeight, start * rowHeight);
  const bottomSpacerHeight = Math.max(0, rowsHeight - end * rowHeight);
  const rows = useMemo(
    () =>
      Array.from({ length: end - start }, (_, index) => {
        const offset = (start + index) * COLUMNS;
        return tiles.slice(offset, offset + COLUMNS);
      }),
    [end, start, tiles],
  );
  // One panels navigator for the whole grid rather than one per tile.
  const pushMutateAsync = useMorphemePanelsHrefNavigator().push.mutateAsync;
  const onNavigate = useCallback<NavigateToMorpheme>(
    (morpheme) =>
      pushMutateAsync({
        morpheme,
        prefix: { href: DECK_MORPHEME_HREF, params: { view: "mnemonics" } },
      }),
    [pushMutateAsync],
  );

  return (
    <View
      className="p-1"
      onLayout={({ nativeEvent: { layout } }) => {
        if (layout.width > 0) {
          setContainerWidth(layout.width);
        }
      }}
      testID="deck-glyph-window"
    >
      <MorphemeTilePresentationProvider>
        {topSpacerHeight > 0 && (
          <View style={{ height: topSpacerHeight }} testID="deck-glyph-leading-space" />
        )}
        {rows.map((row, index) => (
          <View
            className={start + index === rowCount - 1 ? undefined : "pb-1"}
            key={row.map((tile) => tile.userCardId).join("\u0000")}
            testID="deck-glyph-row"
          >
            <DeckGlyphRow
              onNavigate={onNavigate}
              stageByGrapheme={stageByGrapheme}
              tiles={row}
              tileSize={tileSize}
            />
          </View>
        ))}
        {bottomSpacerHeight > 0 && (
          <View style={{ height: bottomSpacerHeight }} testID="deck-glyph-trailing-space" />
        )}
      </MorphemeTilePresentationProvider>
      {footer}
    </View>
  );
}

function DeckGlyphRow({
  onNavigate,
  stageByGrapheme,
  tiles,
  tileSize,
}: {
  onNavigate: NavigateToMorpheme;
  stageByGrapheme: MorphemeStagesByGrapheme;
  tiles: readonly DeckGlyphGridTile[];
  tileSize: number;
}) {
  return (
    <View className="flex-row" style={{ gap: GAP }}>
      {tiles.map((tile) => (
        <View className="min-w-0 flex-1" key={tile.userCardId}>
          <DeckCardMinimalTileLink
            onNavigate={onNavigate}
            size={tileSize}
            stage={stageByGrapheme[tile.front] ?? "unadded"}
            tile={tile}
          />
        </View>
      ))}
      {filledArray(
        (index) => (
          <View className="flex-1" key={`empty-${index}`} />
        ),
        COLUMNS - tiles.length,
      )}
    </View>
  );
}

function DeckCardMinimalTileLink({
  onNavigate,
  size,
  stage,
  tile,
}: {
  onNavigate: NavigateToMorpheme;
  size: number;
  stage: MorphemeGlyphTileStage;
  tile: DeckGlyphGridTile;
}) {
  const selectionItems = useMemo(
    () => [
      userCardSelectionItem({
        contentKind: tile.contentKind,
        front: tile.front,
        userCardId: tile.userCardId,
      }),
    ],
    [tile],
  );
  const { consumePress, isSelected, onLongPress } = useAppSelection(selectionItems);
  const [pendingCount, setPendingCount] = useState(0);
  const handlePress = useCallback(() => {
    if (consumePress()) {
      return;
    }
    setPendingCount((count) => count + 1);
    const settle = () => setPendingCount((count) => count - 1);
    // Both branches release the tile's loading state; the navigator owns error reporting.
    void onNavigate(tile.front).then(settle, settle);
  }, [consumePress, onNavigate, tile.front]);

  return (
    <CardMinimalTileUi
      cardType={tile.cardType}
      glyph={tile.front}
      isLoading={pendingCount > 0}
      isSelected={isSelected}
      onLongPress={onLongPress}
      onPress={handlePress}
      // A pronunciation card is coloured by its own reading (干 gan4 apart from 干 gan1).
      primaryPinyin={tile.pinyin}
      size={size}
      stage={stage}
      // Content-based like the detailed grid's `card-details-tile-<type>-<front>`.
      testID={`deck-card-minimal-tile-${tile.cardType}-${tile.front}`}
      // Tone colors mark pronunciation cards; meaning glyphs stay plain so the two read apart.
      uncolored={tile.cardType === "meaning"}
    />
  );
}

/**
 * Loading placeholder with the view's exact geometry: the same inset, 8 `flex-1` square cells per
 * row with 4px gutters, and `tileCount` tiles (the last row partially empty, like the loaded
 * grid) capped at `maxRowCount` rows.
 */
export function DeckCardSectionGlyphTilesSkeleton({
  maxRowCount = 6,
  tileCount,
}: {
  maxRowCount?: number;
  tileCount: number;
}) {
  const rowCount = Math.min(maxRowCount, Math.ceil(tileCount / COLUMNS));
  return (
    <View className="p-1" testID="deck-glyph-skeleton">
      {Array.from({ length: rowCount }, (_, row) => (
        <View
          className={cn("flex-row", row < rowCount - 1 && "pb-1")}
          key={row}
          style={{ gap: GAP }}
        >
          {Array.from({ length: COLUMNS }, (_, column) =>
            row * COLUMNS + column < tileCount ? (
              <SkeletonTile key={column} />
            ) : (
              <View className="flex-1" key={column} />
            ),
          )}
        </View>
      ))}
    </View>
  );
}
